import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FleetSection } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatCount } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { getCatalogue } from '../../../../api-new/state/notify'
import { CATEGORIES, CHANNELS, categoryLabel, channelLabel, priorityLabel } from '../../../../shared/notify/words'
import { rolloutPath } from './paths'

const yesNo = v => (v ? 'Yes' : 'No')

// "5 minutes", "7 days": the notification's lifetime in words.
export const lifetimeText = seconds => {
  if (!seconds) return 'Does not expire'
  const units = [[86400, 'day'], [3600, 'hour'], [60, 'minute']]
  const [size, name] = units.find(([s]) => seconds >= s && seconds % s === 0) || [1, 'second']
  const n = seconds / size
  return `${formatCount(n)} ${name}${n === 1 ? '' : 's'}`
}

// The coalescing rule in one sentence.
export const coalesceText = c => {
  if (!c || !c.enabled) return 'Not coalesced'
  const parts = [`Within ${lifetimeText(c.windowSeconds)}, by ${c.by}, counting ${c.counts}`]
  if (c.digestAbove) parts.push(`a digest above ${formatCount(c.digestAbove)}`)
  return parts.join('; ')
}

export const matches = (t, { text, category, channel }) => {
  if (category && t.category !== category) return false
  if (channel && !(t.channels || []).includes(channel)) return false
  const needle = text.trim().toLowerCase()
  return !needle || t.type.toLowerCase().includes(needle) || (t.audience || '').toLowerCase().includes(needle)
}

const Field = ({ label, children }) =>
  <div className="d-flex gap-2 mb-1"><dt className="fw-semibold m-0" style={{ minWidth: '9rem' }}>{label}</dt><dd className="m-0">{children}</dd></div>

const Copy = ({ copy }) => {
  if (!copy) return <span className="sw-hint">None</span>
  return (
    <div>
      <div><strong>{copy.title}</strong></div>
      <div>{copy.body}</div>
      {(copy.pluralTitle || copy.pluralBody) && <div className="sw-hint">{`Several at once: ${copy.pluralTitle || ''} / ${copy.pluralBody || ''}`}</div>}
      {(copy.variants || []).map(v =>
        <div key={v.when} className="sw-hint">{`When ${v.when}: ${v.title} / ${v.body}`}</div>)}
      {copy.link && <div className="sw-hint">{`Link: `}<span className="sw-mono">{copy.link}</span></div>}
    </div>
  )
}

const TypeCard = ({ t }) => {
  const sw = t.switch || {}
  const fields = (t.schema && t.schema.fields) || []
  return (
    <article className="border rounded p-3 mb-3" aria-label={t.type} data-testid={`type-${t.type}`}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h3 className="h6 fw-semibold m-0 sw-mono">{t.type}</h3>
        <Pill>{categoryLabel(t.category)}</Pill>
        <Pill tone={t.priority === 'critical' ? 'bad' : t.priority === 'high' ? 'warn' : null}>{priorityLabel(t.priority)}</Pill>
        {t.security && <Pill tone="warn">Security</Pill>}
        <span className="ms-auto d-flex align-items-center gap-2">
          <Pill tone={sw.effective === false ? 'bad' : 'ok'}>{sw.effective === false ? 'Switched off' : 'On'}</Pill>
          {sw.overrides > 0 && <span className="sw-hint">{`${sw.overrides} organization ${sw.overrides === 1 ? 'override' : 'overrides'}`}</span>}
          <Link to={rolloutPath(sw.key || `type:${t.type}`)}>Open in Rollout</Link>
        </span>
      </div>
      <dl className="m-0">
        <Field label="Audience">{t.audience}</Field>
        <Field label="Channels">{(t.channels || []).map(channelLabel).join(', ') || 'None'}</Field>
        <Field label="Inbox">{yesNo(t.inbox)}</Field>
        <Field label="Bulk">{yesNo(t.bulk)}</Field>
        <Field label="Coalescing">{coalesceText(t.coalesce)}</Field>
        <Field label="Lifetime">{lifetimeText(t.expiresInSeconds)}</Field>
        <Field label="Skips the sender">{yesNo(t.excludeActor)}</Field>
        <Field label="Ignores preferences">{yesNo(t.bypassPreferences)}</Field>
        <Field label="Copy"><Copy copy={t.copy} /></Field>
        <Field label="Email template">{t.emailKey ? <span className="sw-mono">{t.emailKey}</span> : <span className="sw-hint">None</span>}</Field>
        <Field label="Payload fields">
          {fields.length === 0
            ? <span className="sw-hint">None</span>
            : <span>{fields.map(f => `${f.key} (${f.type}${f.required ? ', required' : ''}${f.class ? `, ${f.class}` : ''})`).join('; ')}</span>}
        </Field>
      </dl>
    </article>
  )
}

// The catalogue (spec section 13.1, read-only): every notification type the
// platform raises, with its switch state and a link to its row in Rollout,
// and the account emails that no switch can block.
export const CatalogueTab = () => {
  const [catalogue, setCatalogue] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [filter, setFilter] = useState({ text: '', category: '', channel: '' })

  useEffect(() => {
    let live = true
    getCatalogue()
      .then(c => { if (live) setCatalogue(c || { types: [] }) })
      .catch(err => { if (live) setError(serverMessage(err, "The catalogue couldn't be loaded."), err) })
    return () => { live = false }
  }, [setError])

  const types = useMemo(() => (catalogue ? (catalogue.types || []).filter(t => matches(t, filter)) : []), [catalogue, filter])
  const set = (k, v) => setFilter(f => ({ ...f, [k]: v }))

  if (!catalogue) {
    return error
      ? <><p className="text-danger small" role="alert">{error}</p><ErrorReference error={failure} /></>
      : <p className="sw-hint" role="status">Loading the catalogue…</p>
  }

  const emails = catalogue.accountEmails

  return (
    <>
      <FleetSection id="nf-catalogue" title="Notification types" lede="Every notification the platform can raise: who gets it, on which channels, what it says and whether it is switched on.">
        <div className="nf-toolbar">
          <span className="nf-field">
            <label htmlFor="nf-cat-search">Search</label>
            <input id="nf-cat-search" type="search" className="form-control form-control-sm" placeholder="course.published" value={filter.text} onChange={e => set('text', e.target.value)} />
          </span>
          <select className="form-select form-select-sm" aria-label="Category" value={filter.category} onChange={e => set('category', e.target.value)}>
            <option value="">Every category</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
          <select className="form-select form-select-sm" aria-label="Channel" value={filter.channel} onChange={e => set('channel', e.target.value)}>
            <option value="">Every channel</option>
            {CHANNELS.map(c => <option key={c} value={c}>{channelLabel(c)}</option>)}
          </select>
          <span className="sw-hint" role="status">{`${types.length} of ${(catalogue.types || []).length} types`}</span>
        </div>
        {types.length === 0 ? <p className="sw-hint">No notification type matches.</p> : types.map(t => <TypeCard key={t.type} t={t} />)}
      </FleetSection>

      {emails &&
        <FleetSection id="nf-account-emails" title="Account emails" lede="Sent straight from the account flows, outside the notification types above.">
          <p>{`Not switchable: governed by ${emails.governedBy}, so a switch can never block password resets or invites.`}</p>
          {emails.note && <p className="sw-hint">{emails.note}</p>}
          <table className="nf-table">
            <thead><tr><th>Key</th><th>Type</th><th>Category</th><th>Priority</th><th>Schema</th></tr></thead>
            <tbody>
              {(emails.keys || []).map(k =>
                <tr key={k.key}>
                  <td className="sw-mono">{k.key}</td>
                  <td className="sw-mono">{k.type}</td>
                  <td>{categoryLabel(k.category)}</td>
                  <td>{priorityLabel(k.priority)}</td>
                  <td>{`v${k.schemaVersion}`}</td>
                </tr>)}
            </tbody>
          </table>
        </FleetSection>}
    </>
  )
}
